import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Avatar, Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, Tabs, useAsync, useToast } from '../components/ui';
import { AuthImage } from '../components/AuthImage';
import { RowActions } from '../components/RowActions';
import { PropertyAnalytics } from './PropertyAnalytics';
import { api, downloadFile } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDateTime, formatDuration, formatNumber, formatPercent, STATUS_VARIANTS } from '../lib/format';
import { IconAlert, IconBuilding, IconPlus } from '../components/icons';
import {
  METER_CAPACITY_UNIT,
  METER_KIND_LABELS,
  METER_KIND_USES_CAPACITY,
  UTILITY_KINDS,
  type UtilityKind,
} from '../lib/utilityKinds';

type PropertyTab = 'akte' | 'auswertung' | 'qr' | 'objektcheck' | 'verbrauchsdaten';

/** Prozent der Kapazität, ab der ein Öl-/Pellets-Zähler als knapp gilt. */
const CAPACITY_WARN_PERCENT = 25;
const CAPACITY_CRITICAL_PERCENT = 10;

function fillPercent(meter: any): number | null {
  if (!METER_KIND_USES_CAPACITY[meter.kind as UtilityKind] || !meter.capacity || meter.latest_reading_value == null) {
    return null;
  }
  return (meter.latest_reading_value / meter.capacity) * 100;
}

export function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [tab, setTab] = useState<PropertyTab>('akte');
  const property = useAsync(() => api.get(`/api/properties/${id}`), [id]);
  const meters = useAsync(() => api.get(`/api/properties/${id}/meters`), [id]);

  if (property.loading) return <Loading />;
  if (property.error) return <ErrorMessage error={property.error} />;
  if (!property.data) return <EmptyState title="Objekt nicht gefunden" />;

  const data = property.data;
  const qrUrl = data.qr_token ? `${window.location.origin}/melden/${data.qr_token}` : null;
  const lowMeters = (meters.data ?? []).filter((meter: any) => {
    const percent = fillPercent(meter);
    return percent != null && percent <= CAPACITY_WARN_PERCENT;
  });

  const tabs: { key: PropertyTab; label: string }[] = [];
  if (can('reports.operational')) tabs.push({ key: 'auswertung', label: 'Übersicht' });
  tabs.push({ key: 'akte', label: 'Aufträge Objekt' });
  tabs.push({ key: 'objektcheck', label: 'Übersicht Verbraucher' });
  tabs.push({ key: 'verbrauchsdaten', label: 'Verbrauchsdaten' });
  tabs.push({ key: 'qr', label: 'QR-Code' });

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconBuilding width={22} height={22} />}
        title={data.name}
        subtitle={
          <>
            {data.street}, {data.zip} {data.city} · {data.property_type ?? 'Objekt'}
            {data.customer_name ? ` · Eigentümer/Verwalter: ${data.customer_name}` : ''}
          </>
        }
        pill="Objektakte"
        tone="accent"
        actions={
          <>
            <button className="btn" onClick={() => navigate('/properties')}>
              Zurück
            </button>
            {can('tasks.create') && (
              <button className="btn primary" onClick={() => navigate(`/tasks/new?propertyId=${data.id}`)}>
                Meldung erfassen
              </button>
            )}
          </>
        }
      />

      {/* Die Auswertung sitzt in der Akte, nicht im Auswertungsmodul: Wer nach
          den Zahlen eines Objekts fragt, hat das Objekt bereits offen. */}
      <Tabs active={tab} onChange={(value) => setTab(value as PropertyTab)} tabs={tabs} />

      {tab === 'auswertung' ? (
        <PropertyAnalytics propertyId={data.id} />
      ) : tab === 'qr' ? (
        <QrCodeTab property={data} canManage={can('properties.update')} onChanged={() => property.reload()} />
      ) : tab === 'objektcheck' ? (
        <PropertyMetersTab property={data} canManage={can('properties.update')} meters={meters} />
      ) : tab === 'verbrauchsdaten' ? (
        <MeterReadingsTab property={data} />
      ) : (
        <>
      {lowMeters.length > 0 && (
        <div className={`alert ${lowMeters.some((meter: any) => (fillPercent(meter) ?? 100) <= CAPACITY_CRITICAL_PERCENT) ? 'danger' : 'warning'}`}>
          <IconAlert width={16} height={16} />
          <span>
            Niedriger Füllstand:{' '}
            {lowMeters
              .map((meter: any) => `${METER_KIND_LABELS[meter.kind as UtilityKind] ?? meter.kind} ${formatPercent(fillPercent(meter) ?? 0)}`)
              .join(', ')}{' '}
            – siehe Register „Übersicht Verbraucher“.
          </span>
        </div>
      )}
      <div className="grid cols-4">
        <div className="kpi">
          <span className="kpi-label">Offene Aufträge</span>
          <span className="kpi-value">{data.openTasks?.length ?? 0}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Aufträge gesamt</span>
          <span className="kpi-value">{data.stats?.tasksTotal ?? 0}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Letzte 30 Tage</span>
          <span className="kpi-value">{data.stats?.tasksLast30 ?? 0}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Einheiten</span>
          <span className="kpi-value">{data.units_count ?? '–'}</span>
        </div>
      </div>

      <div className="detail-layout">
        <div className="stack">
          <Card title="Offene Vorgänge" noBody>
            {(data.openTasks ?? []).length === 0 ? (
              <EmptyState title="Keine offenen Vorgänge" />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Nummer</th>
                      <th>Titel</th>
                      <th>Status</th>
                      <th>Fällig</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.openTasks.map((task: any) => (
                      <tr key={task.id} className="clickable" onClick={() => navigate(`/tasks/${task.id}`)}>
                        <td className="mono">{task.task_no}</td>
                        <td>{task.title}</td>
                        <td>
                          <Badge variant={STATUS_VARIANTS[task.status] ?? ''}>{task.status}</Badge>
                        </td>
                        <td className="small">{formatDateTime(task.due_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {(data.slaOverrides ?? []).length > 0 && (
            <Card title="Abweichende SLA" subtitle="Vertraglich vereinbarte Fristen für dieses Objekt" noBody>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Kritikalität</th>
                      <th className="num">Reaktion</th>
                      <th className="num">Erledigung</th>
                      <th>Hinweis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.slaOverrides.map((override: any) => (
                      <tr key={override.id}>
                        <td>{override.criticality_name}</td>
                        <td className="num">{formatDuration(override.response_minutes)}</td>
                        <td className="num">{formatDuration(override.resolution_minutes)}</td>
                        <td className="small subtle">{override.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>

        <div className="stack">
          <Card title="Kontakt & Zugang">
            <dl className="definition-list">
              <dt>Ansprechpartner</dt>
              <dd>{data.contact_name ?? '–'}</dd>
              <dt>Telefon</dt>
              <dd>{data.contact_phone ?? '–'}</dd>
              <dt>E-Mail</dt>
              <dd>{data.contact_email ?? '–'}</dd>
              <dt>Referenz</dt>
              <dd className="mono">{data.reference_no ?? '–'}</dd>
            </dl>
            <div style={{ marginTop: 14 }}>
              <div className="small muted" style={{ marginBottom: 4 }}>
                Zugang / Schlüssel
              </div>
              {data.access_notes ? (
                <div className="alert warning" style={{ fontSize: 12.5 }}>
                  {data.access_notes}
                </div>
              ) : (
                <div className="small subtle">
                  Nicht sichtbar – hierfür wird das Recht <span className="mono">properties.access</span> benötigt.
                </div>
              )}
            </div>
          </Card>

          <Card title="Zuständigkeiten">
            {(data.responsibles ?? []).length === 0 && (data.groups ?? []).length === 0 ? (
              <div className="small subtle">Keine Zuständigkeiten hinterlegt.</div>
            ) : (
              <div className="stack" style={{ gap: 8 }}>
                {(data.responsibles ?? []).map((person: any) => (
                  <div className="row" key={person.id} style={{ gap: 8, flexWrap: 'nowrap' }}>
                    <Avatar firstName={person.first_name} lastName={person.last_name} size="sm" />
                    <span>
                      {person.first_name} {person.last_name}
                    </span>
                    {person.is_primary ? <Badge variant="primary">Hauptzuständig</Badge> : null}
                  </div>
                ))}
                {(data.groups ?? []).map((group: any) => (
                  <Badge key={group.id} variant="info">
                    Gruppe: {group.name}
                  </Badge>
                ))}
              </div>
            )}
          </Card>

          <Card title="QR-Code-Meldung" subtitle="Niederschwellige Meldung durch Mieter ohne Login">
            {qrUrl ? (
              <div className="row" style={{ gap: 12, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                <div style={{ width: 72, height: 72, background: '#fff', padding: 6, borderRadius: 8, flexShrink: 0 }}>
                  <AuthImage
                    path={`/api/properties/${data.id}/qr.svg`}
                    alt={`QR-Code ${data.name}`}
                    style={{ width: '100%', height: '100%' }}
                  />
                </div>
                <div>
                  <p className="small muted">
                    Der Aushang am Objekt verweist auf folgende Adresse. Meldungen darüber laufen anonym über den
                    Kanal „QR-Code am Objekt“ ein.
                  </p>
                  <code className="mono small" style={{ wordBreak: 'break-all' }}>
                    {qrUrl}
                  </code>
                </div>
              </div>
            ) : (
              <p className="small subtle">
                Für dieses Objekt ist noch kein QR-Code angelegt – im Register „QR-Code“ oben lässt sich einer
                anlegen.
              </p>
            )}
          </Card>
        </div>
      </div>
        </>
      )}
    </div>
  );
}

/**
 * QR-Code-Verwaltung für genau dieses Objekt – dieselben Bausteine wie die
 * mandantenweite Liste unter Systemeinstellungen → QR-Codes
 * (`QrSettings.tsx`), hier auf ein Objekt bezogen, damit man dafür nicht
 * erst die ganze Objektliste durchsuchen muss.
 */
function QrCodeTab({
  property,
  canManage,
  onChanged,
}: {
  property: any;
  canManage: boolean;
  onChanged: () => void;
}) {
  const { notify } = useToast();
  const [busy, setBusy] = useState(false);
  const hasToken = !!property.qr_token;

  const rotate = async () => {
    const question = hasToken
      ? `Token für „${property.name}“ erneuern? Der bisherige Aushang zeigt danach auf eine ungültige Adresse und muss ausgetauscht werden.`
      : `QR-Code für „${property.name}“ anlegen?`;
    if (!window.confirm(question)) return;
    setBusy(true);
    try {
      await api.post(`/api/properties/${property.id}/qr-token/rotate`);
      notify(hasToken ? 'Token erneuert.' : 'QR-Code angelegt.', 'success');
      onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Aktion nicht möglich.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (
      !window.confirm(
        `QR-Code für „${property.name}“ löschen? Der bisherige Aushang zeigt danach ins Leere, ohne Ersatzcode.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/api/properties/${property.id}/qr-token`);
      notify('QR-Code gelöscht.', 'success');
      onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Aktion nicht möglich.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="QR-Code"
      subtitle="Führt ohne Anmeldung zur öffentlichen Meldeseite dieses Objekts"
      actions={<Badge variant={hasToken ? 'success' : 'warning'}>{hasToken ? 'vorhanden' : 'fehlt'}</Badge>}
    >
      <div className="row" style={{ gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {hasToken && (
          <div style={{ width: 200, height: 200, background: '#fff', padding: 12, borderRadius: 8, flexShrink: 0 }}>
            <AuthImage
              path={`/api/properties/${property.id}/qr.svg`}
              alt={`QR-Code ${property.name}`}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        )}
        <div className="stack" style={{ gap: 10, flex: 1, minWidth: 220 }}>
          {hasToken ? (
            <code className="mono small" style={{ wordBreak: 'break-all' }}>
              {`${window.location.origin}/melden/${property.qr_token}`}
            </code>
          ) : (
            <p className="small subtle">Für dieses Objekt ist noch kein QR-Code angelegt.</p>
          )}

          <div className="row" style={{ gap: 8 }}>
            {canManage && (
              <button className="btn sm" disabled={busy} onClick={rotate}>
                {hasToken ? 'Token erneuern' : 'QR-Code anlegen'}
              </button>
            )}
            {canManage && hasToken && (
              <button className="btn sm ghost" disabled={busy} onClick={remove}>
                Löschen
              </button>
            )}
            {hasToken && (
              <>
                <button
                  className="btn sm"
                  onClick={() => downloadFile(`/api/properties/${property.id}/qr.svg`, `qr-code-${property.id}.svg`)}
                >
                  SVG herunterladen
                </button>
                <button
                  className="btn sm"
                  onClick={() => downloadFile(`/api/properties/${property.id}/qr.pdf`, `qr-aushang-${property.id}.pdf`)}
                >
                  Druckblatt (PDF)
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * Objekt-Check: Verbrauchserfassung (Kap. 7).
 *
 * Erster Ausbauschritt eines Registers, das Raum für spätere Erweiterungen
 * lässt – daher der offenere Name statt „Verbrauchserfassung“. Jeder Zähler
 * hängt an genau diesem Objekt und lässt sich optional mit einem Versorger
 * und einem zur Sparte passenden Tarif verknüpfen.
 */
function PropertyMetersTab({
  property,
  canManage,
  meters,
}: {
  property: any;
  canManage: boolean;
  meters: { data: any[] | null; loading: boolean; error: unknown; reload: () => void };
}) {
  const { notify } = useToast();
  const [modal, setModal] = useState<string | null>(null);
  const suppliers = useAsync(() => api.get('/api/utilities/suppliers'), []);
  const tariffs = useAsync(() => api.get('/api/utilities/tariffs'), []);
  const articles = useAsync(() => api.get('/api/material/articles'), []);

  const list: any[] = meters.data ?? [];

  return (
    <Card
      title="Objekt-Check"
      subtitle="Verbrauchszähler dieses Objekts – Strom, Wasser, Gas, Fernwärme, Öl und Pellets"
      actions={
        canManage && (
          <button className="btn sm primary" onClick={() => setModal('neu')}>
            <IconPlus width={14} height={14} /> Zähler hinzufügen
          </button>
        )
      }
    >
      {meters.loading ? (
        <Loading />
      ) : list.length === 0 ? (
        <EmptyState title="Noch kein Zähler erfasst" />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Sparte</th>
                <th>Nummer / Kapazität</th>
                <th>Versorger</th>
                <th>Tarif</th>
                <th>Notiz</th>
                {canManage && <th className="actions">Aktionen</th>}
              </tr>
            </thead>
            <tbody>
              {list.map((meter: any) => {
                const kind = meter.kind as UtilityKind;
                const usesCapacity = METER_KIND_USES_CAPACITY[kind];
                const percent = fillPercent(meter);
                return (
                  <tr key={meter.id}>
                    <td>{METER_KIND_LABELS[kind] ?? meter.kind}</td>
                    <td className="mono small">
                      {usesCapacity
                        ? meter.capacity != null
                          ? `${formatNumber(meter.capacity, 2)} ${METER_CAPACITY_UNIT[kind]}`
                          : '–'
                        : meter.meter_number ?? '–'}
                      {percent != null && (
                        <>
                          {' '}
                          <Badge variant={percent <= CAPACITY_CRITICAL_PERCENT ? 'danger' : percent <= CAPACITY_WARN_PERCENT ? 'warning' : 'success'}>
                            {formatPercent(percent)}
                          </Badge>
                        </>
                      )}
                    </td>
                    <td className="small subtle">{meter.supplier_name ?? '–'}</td>
                    <td className="small subtle">{meter.tariff_name ?? '–'}</td>
                    <td className="small subtle truncate" style={{ maxWidth: 200 }}>
                      {meter.note ?? '–'}
                    </td>
                    {canManage && (
                      <td className="actions">
                        <RowActions
                          label={METER_KIND_LABELS[kind] ?? meter.kind}
                          onEdit={() => setModal(`bearbeiten-${meter.id}`)}
                          deletePath={`/api/properties/${property.id}/meters/${meter.id}`}
                          confirmText={`${METER_KIND_LABELS[kind] ?? meter.kind} löschen?`}
                          onDeleted={() => meters.reload()}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(modal === 'neu' || modal?.startsWith('bearbeiten-')) && (
        <MeterModal
          property={property}
          meter={list.find((entry: any) => String(entry.id) === modal?.replace('bearbeiten-', ''))}
          suppliers={suppliers.data ?? []}
          tariffs={tariffs.data ?? []}
          articles={articles.data ?? []}
          onClose={() => setModal(null)}
          onSaved={() => {
            const wasNew = modal === 'neu';
            setModal(null);
            meters.reload();
            notify(wasNew ? 'Zähler angelegt.' : 'Zähler gespeichert.', 'success');
          }}
        />
      )}
    </Card>
  );
}

function MeterModal({
  property,
  meter,
  suppliers,
  tariffs,
  articles,
  onClose,
  onSaved,
}: {
  property: any;
  meter?: any;
  suppliers: any[];
  tariffs: any[];
  articles: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    kind: (meter?.kind ?? 'electricity') as UtilityKind,
    meterNumber: meter?.meter_number ?? '',
    capacity: meter?.capacity != null ? String(meter.capacity) : '',
    supplierId: meter?.supplier_id ? String(meter.supplier_id) : '',
    tariffId: meter?.tariff_id ? String(meter.tariff_id) : '',
    defaultArticleId: meter?.default_article_id ? String(meter.default_article_id) : '',
    note: meter?.note ?? '',
  });
  const [error, setError] = useState<unknown>(null);
  const set = (key: keyof typeof form, value: any) => setForm((current) => ({ ...current, [key]: value }));

  const usesCapacity = METER_KIND_USES_CAPACITY[form.kind];
  const passendeTarife = tariffs.filter(
    (entry: any) => entry.kind === form.kind && (!form.supplierId || String(entry.supplier_id) === form.supplierId),
  );

  return (
    <Modal
      title={meter ? `Zähler bearbeiten – ${METER_KIND_LABELS[form.kind]}` : 'Zähler hinzufügen'}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            onClick={async () => {
              try {
                const payload = {
                  kind: form.kind,
                  meterNumber: usesCapacity ? null : form.meterNumber || null,
                  capacity: usesCapacity && form.capacity ? Number(form.capacity) : null,
                  supplierId: form.supplierId ? Number(form.supplierId) : null,
                  tariffId: form.tariffId ? Number(form.tariffId) : null,
                  defaultArticleId: usesCapacity && form.defaultArticleId ? Number(form.defaultArticleId) : null,
                  note: form.note || null,
                };
                if (meter) await api.patch(`/api/properties/${property.id}/meters/${meter.id}`, payload);
                else await api.post(`/api/properties/${property.id}/meters`, payload);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Sparte" required>
            <select
              value={form.kind}
              onChange={(event) => {
                const kind = event.target.value as UtilityKind;
                // Ein Tarif für die alte Sparte passt zur neuen nicht mehr.
                const nochPassend = tariffs.find((entry: any) => String(entry.id) === form.tariffId)?.kind === kind;
                setForm((current) => ({ ...current, kind, tariffId: nochPassend ? current.tariffId : '' }));
              }}
            >
              {UTILITY_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {METER_KIND_LABELS[kind]}
                </option>
              ))}
            </select>
          </Field>
          {usesCapacity ? (
            <Field label={`Kapazität (${METER_CAPACITY_UNIT[form.kind]})`}>
              <input type="number" min="0" step="0.01" value={form.capacity} onChange={(event) => set('capacity', event.target.value)} />
            </Field>
          ) : (
            <Field label="Zählernummer">
              <input value={form.meterNumber} onChange={(event) => set('meterNumber', event.target.value)} />
            </Field>
          )}
          <Field label="Versorger">
            <SearchSelect
              value={form.supplierId}
              onChange={(value) => {
                // Ein Tarif eines anderen Versorgers passt danach nicht mehr.
                const nochPassend = tariffs.find((entry: any) => String(entry.id) === form.tariffId)?.supplier_id === (value ? Number(value) : null);
                setForm((current) => ({ ...current, supplierId: value, tariffId: nochPassend ? current.tariffId : '' }));
              }}
              options={suppliers.map((entry: any) => ({ value: String(entry.id), label: entry.name }))}
              placeholder="– keiner –"
            />
          </Field>
          <Field label="Tarif" hint={!passendeTarife.length ? 'Kein passender Tarif für diese Sparte/diesen Versorger.' : undefined}>
            <SearchSelect
              value={form.tariffId}
              onChange={(value) => set('tariffId', value)}
              options={passendeTarife.map((entry: any) => ({ value: String(entry.id), label: `${entry.name} · ${entry.supplier_name}` }))}
              placeholder="– keiner –"
            />
          </Field>
          {usesCapacity && (
            <Field label="Artikel für Nachbestellung" hint="Wird bei automatischer Nachbestellung unter 25 %/10 % Füllstand verwendet.">
              <SearchSelect
                value={form.defaultArticleId}
                onChange={(value) => set('defaultArticleId', value)}
                options={articles.map((entry: any) => ({ value: String(entry.id), label: `${entry.article_no} · ${entry.name}` }))}
                placeholder="– keiner –"
              />
            </Field>
          )}
        </div>
        <Field label="Notiz">
          <input value={form.note} onChange={(event) => set('note', event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Register „Verbrauchsdaten“: vollständige Ablesehistorie dieses Objekts,
 * sortierbar nach Datum, gruppierbar nach Typ und/oder Zählernummer, mit
 * CSV-Export über einen Zeitraum oder für alle Daten.
 */
function MeterReadingsTab({ property }: { property: any }) {
  const readings = useAsync(() => api.get(`/api/properties/${property.id}/readings`), [property.id]);
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [groupBy, setGroupBy] = useState<'none' | 'kind' | 'meter' | 'both'>('none');
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');

  const list: any[] = readings.data ?? [];
  const sorted = [...list].sort((a, b) =>
    sortDir === 'desc' ? b.reading_date.localeCompare(a.reading_date) : a.reading_date.localeCompare(b.reading_date),
  );

  const groupKey = (row: any) => {
    const kindLabel = METER_KIND_LABELS[row.kind as UtilityKind] ?? row.kind;
    if (groupBy === 'kind') return kindLabel;
    if (groupBy === 'meter') return row.meter_number ?? '–';
    if (groupBy === 'both') return `${kindLabel} · ${row.meter_number ?? '–'}`;
    return null;
  };
  const groups: { label: string | null; rows: any[] }[] =
    groupBy === 'none'
      ? [{ label: null, rows: sorted }]
      : Object.entries(
          sorted.reduce((acc: Record<string, any[]>, row) => {
            const key = groupKey(row) ?? '–';
            (acc[key] ??= []).push(row);
            return acc;
          }, {}),
        ).map(([label, rows]) => ({ label, rows }));

  return (
    <Card
      title="Verbrauchsdaten"
      subtitle="Verlauf aller erfassten Zählerstände und Füllstände dieses Objekts"
      actions={
        <label className="toolbar-field">
          <span>Gruppieren</span>
          <select value={groupBy} onChange={(event) => setGroupBy(event.target.value as typeof groupBy)}>
            <option value="none">ohne</option>
            <option value="kind">nach Typ</option>
            <option value="meter">nach Zählernummer</option>
            <option value="both">nach Typ und Zählernummer</option>
          </select>
        </label>
      }
    >
      <div className="row" style={{ gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label className="toolbar-field">
          <span>Von</span>
          <input type="date" value={exportFrom} onChange={(event) => setExportFrom(event.target.value)} />
        </label>
        <label className="toolbar-field">
          <span>Bis</span>
          <input type="date" value={exportTo} onChange={(event) => setExportTo(event.target.value)} />
        </label>
        <button
          className="btn sm"
          disabled={!exportFrom && !exportTo}
          onClick={() =>
            downloadFile(`/api/properties/${property.id}/readings/export.csv`, `verbrauchsdaten-${property.name}.csv`, {
              from: exportFrom || undefined,
              to: exportTo || undefined,
            })
          }
        >
          Export (Zeitraum)
        </button>
        <button
          className="btn sm"
          onClick={() => downloadFile(`/api/properties/${property.id}/readings/export.csv`, `verbrauchsdaten-${property.name}-alle.csv`)}
        >
          Alle Daten exportieren
        </button>
      </div>

      {readings.loading ? (
        <Loading />
      ) : list.length === 0 ? (
        <EmptyState
          title="Noch keine Verbrauchsdaten erfasst"
          hint="Über „Verbrauchsdatenerfassung“ im Menü lassen sich Zählerstände und Füllstände eintragen."
        />
      ) : (
        <div className="stack">
          {groups.map((group) => (
            <div key={group.label ?? 'alle'}>
              {group.label && (
                <div className="small muted" style={{ margin: '10px 0 4px', fontWeight: 600 }}>
                  {group.label}
                </div>
              )}
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th className="clickable" onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')}>
                        Datum {sortDir === 'desc' ? '↓' : '↑'}
                      </th>
                      <th>Sparte</th>
                      <th>Zählernummer</th>
                      <th className="num">Stand</th>
                      <th>Notiz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((row: any) => (
                      <tr key={row.id}>
                        <td className="small">{formatDate(row.reading_date)}</td>
                        <td>{METER_KIND_LABELS[row.kind as UtilityKind] ?? row.kind}</td>
                        <td className="mono small">{row.meter_number ?? '–'}</td>
                        <td className="num mono small">
                          {formatNumber(row.reading_value, 2)}
                          {METER_KIND_USES_CAPACITY[row.kind as UtilityKind] ? ` ${METER_CAPACITY_UNIT[row.kind as UtilityKind]}` : ''}
                        </td>
                        <td className="small subtle truncate" style={{ maxWidth: 200 }}>
                          {row.note ?? '–'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
